import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { formatDate, formatHoursMinutes } from '../../../lib/format';
import { useReportScope } from '../../../lib/useReportScope';
import MultiSelectFilter from '../../../components/workspace/MultiSelectFilter';
import ReportPdfButton, { ExcelIcon, PdfIcon, iconBtnBase, excelBtnStyle, downloadExcel } from '../../../components/workspace/ReportPdfButton';

const REPORT_TYPES = [
  { value: 'overdue',          label: 'Overdue Tasks'     },
  { value: 'sla',              label: 'SLA Compliance'    },
  { value: 'workload',         label: 'User Workload'     },
  { value: 'agreement-expiry', label: 'Agreement Expiry'  },
  { value: 'revenue',          label: 'Revenue by Client' },
];

function fmt(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtINR(n) {
  if (n == null) return '—';
  return '₹' + Number(n).toLocaleString('en-IN');
}

function StatCard({ value, label, color }) {
  return (
    <div style={{
      flex: 1, minWidth: 180,
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '28px 24px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 36, fontWeight: 800, color: color || 'var(--brand)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function Card({ title, children, style }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '20px 22px',
      ...style,
    }}>
      {title && <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', marginBottom: 16 }}>{title}</div>}
      {children}
    </div>
  );
}

function ReportTable({ cols, rows, emptyMsg }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>{emptyMsg || 'No data.'}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key} style={{
                textAlign: 'left', padding: '8px 12px',
                fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
                textTransform: 'uppercase', letterSpacing: '0.8px',
                borderBottom: '1px solid var(--border)',
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '12px 12px', color: 'var(--text-primary)', verticalAlign: 'middle' }}>
                  {c.render ? c.render(r) : (r[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BarChart({ data, keys, colors, labels, height = 240 }) {
  if (!data || data.length === 0) return null;
  const W = 600, H = height;
  const PAD = { top: 16, right: 20, bottom: 50, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(1, ...data.flatMap(d => keys.map(k => Number(d[k] || 0))));
  const yTicks = [];
  const step = Math.ceil(maxVal / 5);
  for (let v = 0; v <= maxVal + step; v += step) yTicks.push(v);

  const groupW = chartW / data.length;
  const barW   = Math.min(40, (groupW / keys.length) - 4);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: W }}>
      {yTicks.map(v => {
        const y = PAD.top + chartH - (v / (yTicks[yTicks.length - 1])) * chartH;
        return (
          <g key={v}>
            <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} />
            <text x={PAD.left - 6} y={y + 4} fontSize={10} fill="var(--text-muted)" textAnchor="end">{v}</text>
          </g>
        );
      })}

      {data.map((d, gi) => {
        const cx = PAD.left + gi * groupW + groupW / 2;
        const totalBarW = barW * keys.length + (keys.length - 1) * 4;
        return keys.map((k, ki) => {
          const val = Number(d[k] || 0);
          const barH = (val / yTicks[yTicks.length - 1]) * chartH;
          const x = cx - totalBarW / 2 + ki * (barW + 4);
          const y = PAD.top + chartH - barH;
          return (
            <rect key={k} x={x} y={y} width={barW} height={barH} fill={colors[ki]} rx={3}>
              <title>{keys[ki]}: {val}</title>
            </rect>
          );
        });
      })}

      {data.map((d, gi) => {
        const cx = PAD.left + gi * groupW + groupW / 2;
        const name = d.label || d.name || d.client_name || '';
        return (
          <text key={gi} x={cx} y={H - PAD.bottom + 18} fontSize={11} fill="var(--text-secondary)" textAnchor="middle">
            {name.length > 18 ? name.slice(0, 17) + '…' : name}
          </text>
        );
      })}

      {keys.map((k, i) => (
        <g key={k} transform={`translate(${PAD.left + i * 110}, ${H - 14})`}>
          <rect width={10} height={10} fill={colors[i]} rx={2} />
          <text x={14} y={9} fontSize={11} fill="var(--text-secondary)">{labels ? labels[i] : k}</text>
        </g>
      ))}

      <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={PAD.top + chartH} stroke="var(--border)" />
      <line x1={PAD.left} x2={PAD.left + chartW} y1={PAD.top + chartH} y2={PAD.top + chartH} stroke="var(--border)" />
    </svg>
  );
}

function PieChart({ segments, colors, labels }) {
  const total = segments.reduce((s, v) => s + v, 0);
  if (total === 0) return null;
  const cx = 120, cy = 120, r = 90;
  let angle = -Math.PI / 2;
  const slices = segments.map((v, i) => {
    const sweep = (v / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(angle);
    const y1 = cy + r * Math.sin(angle);
    angle += sweep;
    const x2 = cx + r * Math.cos(angle);
    const y2 = cy + r * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    return { d: `M${cx},${cy} L${x1},${y1} A${r},${r},0,${large},1,${x2},${y2} Z`, color: colors[i], label: labels[i], val: v };
  });

  return (
    <svg viewBox="0 0 240 240" style={{ width: 200, height: 200 }}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} stroke="var(--bg-card)" strokeWidth={2}>
          <title>{s.label}: {s.val}</title>
        </path>
      ))}
      {labels.map((l, i) => (
        <g key={i} transform={`translate(20, ${200 + i * 18})`}>
          <rect width={10} height={10} fill={colors[i]} rx={2} />
          <text x={14} y={9} fontSize={11} fill="var(--text-secondary)">{l}: {segments[i]}</text>
        </g>
      ))}
    </svg>
  );
}

function DaysBadge({ days }) {
  const n = Number(days);
  const urgent = n <= 30;
  return (
    <span style={{
      background: urgent ? '#fee2e2' : 'var(--bg-muted)',
      color: urgent ? '#dc2626' : 'var(--text-secondary)',
      borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600,
    }}>
      {n}d
    </span>
  );
}

function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  const map = {
    completed:  { bg: '#d1fae5', color: '#065f46', label: 'COMPLETED'   },
    delayed:    { bg: '#fef3c7', color: '#92400e', label: 'DELAYED'     },
    overdue:    { bg: '#fee2e2', color: '#991b1b', label: 'OVERDUE'     },
    pending:    { bg: 'var(--bg-muted)', color: 'var(--text-secondary)', label: 'PENDING' },
    in_progress:{ bg: '#dbeafe', color: '#1e40af', label: 'IN PROGRESS' },
  };
  const st = map[s] || { bg: 'var(--bg-muted)', color: 'var(--text-secondary)', label: status || '—' };
  return (
    <span style={{ background: st.bg, color: st.color, borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
      {st.label}
    </span>
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: 'var(--bg-muted)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--brand)', borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function OverdueReport({ data }) {
  return (
    <Card title={`Overdue Tasks (${data.length})`}>
      <ReportTable
        emptyMsg="No overdue tasks."
        rows={data}
        cols={[
          { key: 'task_number',        label: 'Task #',       render: r => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{r.task_number}</span> },
          { key: 'client_name',        label: 'Client' },
          { key: 'service_name',       label: 'Service' },
          { key: 'due_date',           label: 'Due Date',     render: r => <span style={{ color: '#dc2626' }}>{fmt(r.due_date)}</span> },
          { key: 'assigned_user_name', label: 'Assigned To',  render: r => r.assigned_user_name || '—' },
          { key: 'days_overdue',       label: 'Days Overdue', render: r => <DaysBadge days={r.days_overdue} /> },
        ]}
      />
    </Card>
  );
}

function SlaReport({ data }) {
  if (!data || data.length === 0) return <Card><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data.</div></Card>;

  const onTime  = data.filter(r => r.status === 'completed').length;
  const delayed = data.filter(r => r.status === 'delayed').length;
  const pct     = data.length > 0 ? Math.round((onTime / data.length) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <StatCard value={`${pct}%`} label="SLA Compliance" color="var(--brand)" />
        <StatCard value={onTime}    label="On Time"         color="#16a34a" />
        <StatCard value={delayed}   label="Delayed"         color="#ea580c" />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Card title="Completion Distribution" style={{ flex: '0 0 280px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <PieChart
              segments={[delayed, onTime]}
              colors={['#4ade80', 'var(--brand)']}
              labels={['Delayed', 'On Time']}
            />
          </div>
        </Card>

        <Card title="SLA Detail" style={{ flex: 1, minWidth: 300, overflowX: 'auto' }}>
          <ReportTable
            rows={data}
            emptyMsg="No SLA data."
            cols={[
              { key: 'task_number',     label: 'Task',      render: r => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{r.task_number}</span> },
              { key: 'client_name',     label: 'Client' },
              { key: 'due_date',        label: 'Due',       render: r => fmt(r.due_date) },
              { key: 'completion_date', label: 'Completed', render: r => fmt(r.completion_date) },
              { key: 'status',          label: 'Status',    render: r => <StatusBadge status={r.status} /> },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}

function WorkloadReport({ data }) {
  if (!data || data.length === 0) return <Card><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data.</div></Card>;

  const chartData = data.map(r => ({
    label:     r.name?.split(' ')[0] || r.email,
    assigned:  r.total     || 0,
    completed: r.completed || 0,
    overdue:   r.overdue   || 0,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="User Task Distribution">
        <BarChart
          data={chartData}
          keys={['assigned', 'completed', 'overdue']}
          colors={['var(--brand)', '#16a34a', '#dc2626']}
          labels={['Assigned', 'Completed', 'Overdue']}
        />
      </Card>

      <Card title="User Workload Detail">
        <ReportTable
          rows={data}
          emptyMsg="No workload data."
          cols={[
            { key: 'name',      label: 'User',         render: r => r.name || r.email },
            { key: 'total',     label: 'Assigned' },
            { key: 'completed', label: 'Completed',    render: r => <span style={{ color: '#16a34a', fontWeight: 600 }}>{r.completed}</span> },
            { key: 'pending',   label: 'Pending',      render: r => <span style={{ color: '#d97706', fontWeight: 600 }}>{r.pending}</span> },
            { key: 'overdue',   label: 'Overdue',      render: r => <span style={{ color: '#dc2626', fontWeight: 600 }}>{r.overdue}</span> },
            { key: 'pct',       label: 'Completion %', render: r => {
              const pct = r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0;
              return <ProgressBar pct={pct} />;
            }},
          ]}
        />
      </Card>
    </div>
  );
}

function ExpiryReport({ data }) {
  if (!data || data.length === 0) return <Card><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No agreements expiring soon.</div></Card>;

  const in30 = data.filter(r => r.days_left <= 30).length;
  const in60 = data.filter(r => r.days_left <= 60).length;
  const in90 = data.filter(r => r.days_left <= 90).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <StatCard value={in30} label="Expiring in 30 days"       color="#ea580c" />
        <StatCard value={in60} label="Expiring in 60 days"       color="#ea580c" />
        <StatCard value={in90} label="Total expiring in 90 days" color="var(--text-primary)" />
      </div>

      <Card title="Agreements Expiring Soon">
        <ReportTable
          rows={data}
          emptyMsg="No agreements expiring in the next 90 days."
          cols={[
            { key: 'agreement_number',  label: 'Agreement',    render: r => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{r.agreement_number}</span> },
            { key: 'client_name',       label: 'Client' },
            { key: 'end_date',          label: 'End Date',     render: r => fmt(r.end_date) },
            { key: 'service_owner_name',label: 'Service Owner',render: r => r.service_owner_name || '—' },
            { key: 'days_left',         label: 'Days Left',    render: r => <DaysBadge days={r.days_left} /> },
          ]}
        />
      </Card>
    </div>
  );
}

function RevenueReport({ data }) {
  if (!data || data.length === 0) return <Card><div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No data.</div></Card>;

  const chartData = data.map(r => ({
    label:   r.client_name,
    revenue: Number(r.expected_billing || r.total_billing || 0),
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Revenue by Client">
        <BarChart
          data={chartData}
          keys={['revenue']}
          colors={['var(--brand)']}
          labels={['Revenue']}
          height={260}
        />
      </Card>

      <Card title="Pricing Detail">
        <ReportTable
          rows={data}
          emptyMsg="No revenue data."
          cols={[
            { key: 'client_name',      label: 'Client' },
            { key: 'service_name',     label: 'Service' },
            { key: 'employee_count',   label: 'Employees' },
            { key: 'base_price',       label: 'Base',           render: r => fmtINR(r.base_price) },
            { key: 'per_employee',     label: 'Add./Emp.',      render: r => fmtINR(r.per_employee) },
            { key: 'expected_billing', label: 'Expected Billing',render: r => (
              <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{fmtINR(r.expected_billing || r.total_billing)}</span>
            )},
          ]}
        />
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 2 — Compliance Reports (overdue, SLA, workload, expiry, revenue)
// ═══════════════════════════════════════════════════════════════════════════════
function ComplianceReportsTab({ appId }) {
  const api = useApi();
  const [type, setType]       = useState('overdue');
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setData([]);
    try {
      const d = await api.getContractReports(appId, { type });
      setData(Array.isArray(d) ? d : (d?.rows || []));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appId, type]);

  useEffect(() => { load(); }, [load]);

  const pdfProps = useMemo(() => {
    const typeMeta = REPORT_TYPES.find(r => r.value === type);
    const title = `Agreements Report — ${typeMeta?.label || type}`;

    if (type === 'overdue') return {
      title, summaryCards: [{ label: 'Overdue Tasks', value: data.length, color: '#dc2626' }],
      table: {
        headers: ['Task #', 'Client', 'Service', 'Due Date', 'Assigned To', 'Days Overdue'],
        rows: data.map(r => [r.task_number, r.client_name, r.service_name, fmt(r.due_date), r.assigned_user_name || '—', `${r.days_overdue}d`]),
      },
    };

    if (type === 'sla') {
      const onTime  = data.filter(r => r.status === 'completed').length;
      const delayed = data.filter(r => r.status === 'delayed').length;
      const pct     = data.length > 0 ? Math.round((onTime / data.length) * 100) : 0;
      return {
        title,
        summaryCards: [
          { label: 'SLA Compliance', value: `${pct}%`, color: 'var(--brand)' },
          { label: 'On Time',        value: onTime,     color: '#16a34a' },
          { label: 'Delayed',        value: delayed,    color: '#ea580c' },
        ],
        table: {
          headers: ['Task #', 'Client', 'Due Date', 'Completed', 'Status'],
          rows: data.map(r => [r.task_number, r.client_name, fmt(r.due_date), fmt(r.completion_date), r.status || '—']),
        },
      };
    }

    if (type === 'workload') return {
      title, summaryCards: [{ label: 'Total Users', value: data.length, color: 'var(--brand)' }],
      table: {
        headers: ['User', 'Assigned', 'Completed', 'Pending', 'Overdue', 'Completion %'],
        rows: data.map(r => {
          const pct = r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0;
          return [r.name || r.email, r.total, r.completed, r.pending, r.overdue, `${pct}%`];
        }),
      },
    };

    if (type === 'agreement-expiry') {
      const in30 = data.filter(r => r.days_left <= 30).length;
      const in60 = data.filter(r => r.days_left <= 60).length;
      return {
        title,
        summaryCards: [
          { label: 'Expiring in 30d', value: in30,        color: '#ea580c' },
          { label: 'Expiring in 60d', value: in60,        color: '#ea580c' },
          { label: 'Total ≤ 90d',     value: data.length, color: 'var(--text-primary)' },
        ],
        table: {
          headers: ['Agreement', 'Client', 'End Date', 'Service Owner', 'Days Left'],
          rows: data.map(r => [r.agreement_number, r.client_name, fmt(r.end_date), r.service_owner_name || '—', `${r.days_left}d`]),
        },
      };
    }

    if (type === 'revenue') return {
      title, summaryCards: [{ label: 'Clients', value: data.length, color: 'var(--brand)' }],
      table: {
        headers: ['Client', 'Service', 'Employees', 'Base', 'Add./Emp.', 'Expected Billing'],
        rows: data.map(r => [r.client_name, r.service_name, r.employee_count, fmtINR(r.base_price), fmtINR(r.per_employee), fmtINR(r.expected_billing || r.total_billing)]),
      },
    };

    return { title, summaryCards: [], table: { headers: [], rows: [] } };
  }, [type, data]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, minHeight: 34 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
          Compliance tasks, SLA performance, workload, and revenue by client.
        </p>
        {!loading && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => downloadExcel(`agreements-report.xls`, pdfProps.title, '', pdfProps.table.headers, pdfProps.table.rows)}
              disabled={!pdfProps.table.rows.length}
              style={{ ...excelBtnStyle, opacity: !pdfProps.table.rows.length ? 0.4 : 1 }}
            >
              <ExcelIcon /> Excel
            </button>
            <ReportPdfButton {...pdfProps} icon={<PdfIcon />} />
          </div>
        )}
      </div>

      {/* Report type tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap' }}>
        {REPORT_TYPES.map(rt => (
          <button
            key={rt.value}
            onClick={() => setType(rt.value)}
            style={{
              padding: '7px 16px',
              borderRadius: 20,
              border: '1px solid',
              borderColor: type === rt.value ? 'var(--brand)' : 'var(--border)',
              background: type === rt.value ? 'var(--brand)' : 'var(--bg-card)',
              color: type === rt.value ? '#fff' : 'var(--text-primary)',
              fontWeight: type === rt.value ? 700 : 500,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >{rt.label}</button>
        ))}
      </div>

      {error   && <div style={{ color: '#dc2626', marginBottom: 16, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 13 }}>Loading…</div>}

      {!loading && (
        <>
          {type === 'overdue'          && <OverdueReport  data={data} />}
          {type === 'sla'              && <SlaReport      data={data} />}
          {type === 'workload'         && <WorkloadReport data={data} />}
          {type === 'agreement-expiry' && <ExpiryReport   data={data} />}
          {type === 'revenue'          && <RevenueReport  data={data} />}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tab 1 — Agreement Hours Report (task hours grouped by agreement)
// ═══════════════════════════════════════════════════════════════════════════════
const TASK_STATUS_LABEL = {
  open:        'Not Started',
  in_progress: 'In Progress',
  blocked:     'Blocked',
  done:        'Completed',
  cancelled:   'Cancelled',
};

const TASK_STATUS_COLOR = {
  open:        '#6b7280',
  in_progress: '#3b82f6',
  blocked:     '#ef4444',
  done:        '#10b981',
  cancelled:   '#9ca3af',
};

const labelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const tdStyle = { padding: '10px 14px', verticalAlign: 'top' };

function TaskStatusBadge({ status }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      background: `${TASK_STATUS_COLOR[status] || '#6b7280'}22`,
      color: TASK_STATUS_COLOR[status] || '#6b7280',
    }}>
      {TASK_STATUS_LABEL[status] || status}
    </span>
  );
}

function Th({ children, right }) {
  return (
    <th style={{
      padding: '10px 14px',
      textAlign: right ? 'right' : 'left',
      fontWeight: 600,
      color: 'var(--text-secondary)',
      whiteSpace: 'nowrap',
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  );
}

function AgreementHoursTab({ appId }) {
  const api = useApi();
  const { me } = useWorkspace();
  const { isAdmin, scopeIds, isManager } = useReportScope(appId);
  const isMultiUser = isAdmin || isManager;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 7) + '-01';
  const [fromDate, setFromDate]                 = useState(firstOfMonth);
  const [toDate,   setToDate]                   = useState(today);
  const [filterClients,    setFilterClients]    = useState([]);
  const [filterAgreements, setFilterAgreements] = useState([]);
  const [filterMembers,    setFilterMembers]    = useState([]);
  const [filterStatus,     setFilterStatus]     = useState('');
  const [members, setMembers] = useState([]);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.listAppMembers(appId)
      .then(r => setMembers(r.items || []))
      .catch(() => {});
  }, [appId]);

  const loadReport = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    try {
      const d = await api.getContractReports(appId, { type: 'agreement-hours', from: fromDate, to: toDate });
      setItems(Array.isArray(d) ? d : (d?.rows || []));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appId, fromDate, toDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const nameMap = useMemo(() => {
    const map = {};
    members.forEach(m => {
      map[m.user_id] = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    });
    if (me) map[me.id] = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;
    return map;
  }, [members, me]);

  const memberName = id => (id == null ? 'Unassigned' : nameMap[id] || `User #${id}`);

  const clientOptions = useMemo(() => {
    const seen = new Map();
    items.forEach(r => { if (!seen.has(r.client_id)) seen.set(r.client_id, r.client_name); });
    return [...seen.entries()].map(([value, label]) => ({ value: String(value), label }));
  }, [items]);

  const agreementOptions = useMemo(() => {
    const seen = new Map();
    items
      .filter(r => filterClients.length === 0 || filterClients.includes(String(r.client_id)))
      .forEach(r => { if (!seen.has(r.agreement_id)) seen.set(r.agreement_id, r.agreement_name); });
    return [...seen.entries()].map(([value, label]) => ({ value: String(value), label }));
  }, [items, filterClients]);

  const filteredItems = useMemo(() => items.filter(r =>
    (filterClients.length    === 0 || filterClients.includes(String(r.client_id))) &&
    (filterAgreements.length === 0 || filterAgreements.includes(String(r.agreement_id))) &&
    (!isMultiUser || filterMembers.length === 0 || filterMembers.includes(String(r.logger_user_id))) &&
    (!filterStatus || r.status === filterStatus)
  ), [items, filterClients, filterAgreements, filterMembers, filterStatus, isMultiUser]);

  // Agreement → tasks → per-logger rows (backend returns rows already ordered this way)
  const agreementGroups = useMemo(() => {
    const groups = [];
    let ag = null, task = null;
    for (const row of filteredItems) {
      if (!ag || ag.agreement_id !== row.agreement_id) {
        ag = { agreement_id: row.agreement_id, agreement_name: row.agreement_name, client_name: row.client_name,
               tasks: [], total_minutes: 0 };
        groups.push(ag);
        task = null;
      }
      if (!task || task.task_id !== row.task_id) {
        task = { task_id: row.task_id, task_number: row.task_number, title: row.title, status: row.status,
                 assigned_to_user_id: row.assigned_to_user_id, rows: [], total_minutes: 0 };
        ag.tasks.push(task);
      }
      task.rows.push(row);
      task.total_minutes += row.user_minutes;
      ag.total_minutes   += row.user_minutes;
    }
    return groups;
  }, [filteredItems]);

  const taskCount         = agreementGroups.reduce((s, a) => s + a.tasks.length, 0);
  const grandTotalMinutes = agreementGroups.reduce((s, a) => s + a.total_minutes, 0);
  const grandTotalHours   = formatHoursMinutes(grandTotalMinutes);
  const periodLabel = fromDate === toDate ? fromDate : `${fromDate} – ${toDate}`;

  const exportHeaders = ['S.No', 'Client Name', 'Agreement Name', 'Task ID', 'Task', 'Status',
    ...(isMultiUser ? ['Assignee'] : []), 'First Log', 'Last Log', 'Time Taken (hrs)'];
  const exportRows = useMemo(() => agreementGroups.flatMap((ag, ai) => [
    ...ag.tasks.flatMap((task, ti) => task.rows.map((row, ri) => {
      const firstOfAg   = ti === 0 && ri === 0;
      const firstOfTask = ri === 0;
      return [
        firstOfAg ? ai + 1 : '',
        firstOfAg ? ag.client_name : '',
        firstOfAg ? ag.agreement_name : '',
        firstOfTask ? (task.task_number || `#${task.task_id}`) : '',
        firstOfTask ? task.title : '',
        firstOfTask ? (TASK_STATUS_LABEL[task.status] || task.status) : '',
        ...(isMultiUser ? [memberName(row.logger_user_id)] : []),
        row.first_log_date ? formatDate(row.first_log_date) : '',
        row.last_log_date  ? formatDate(row.last_log_date)  : '',
        formatHoursMinutes(row.user_minutes),
      ];
    })),
    [...Array(exportHeaders.length - 2).fill(''), 'Agreement Total', formatHoursMinutes(ag.total_minutes)],
  ]), [agreementGroups, isMultiUser, nameMap]);

  const pdfCards = !loading && agreementGroups.length > 0 ? [
    { label: 'Agreements',  value: agreementGroups.length, color: 'var(--text-primary)' },
    { label: 'Tasks',       value: taskCount,              color: '#6f7bff' },
    { label: 'Total Hours', value: grandTotalHours,        color: 'var(--brand)' },
    { label: 'Period',      value: periodLabel,            color: '#4ad295' },
  ] : [];

  const memberOptions = members
    .filter(m => !scopeIds || scopeIds.has(String(m.user_id)))
    .map(m => ({ value: String(m.user_id), label: [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email }));

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
          Hours logged on tasks under each agreement, attributed to whoever <strong>logged the time</strong>.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadExcel(
              `agreement-hours-${fromDate}-${toDate}.xls`,
              'Agreement Hours Report',
              `Period: ${periodLabel}${filterStatus ? `  |  Status: ${TASK_STATUS_LABEL[filterStatus]}` : ''}`,
              exportHeaders,
              [...exportRows, [...Array(exportHeaders.length - 2).fill(''), 'Grand Total', grandTotalHours]]
            )}
            disabled={agreementGroups.length === 0}
            style={{ ...iconBtnBase, background: '#f0faf4', color: '#166534', borderColor: '#bbf7d0', opacity: agreementGroups.length === 0 ? 0.4 : 1 }}
          >
            <ExcelIcon /> Excel
          </button>
          <ReportPdfButton
            title="Agreement Hours Report"
            subtitle={`Period: ${periodLabel}`}
            summaryCards={pdfCards}
            table={{ headers: exportHeaders, rows: exportRows }}
            icon={<PdfIcon />}
          />
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24,
        padding: '16px 20px', background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>From</label>
          <input type="date" className="tl-select" value={fromDate}
            onChange={e => setFromDate(e.target.value)} style={{ minWidth: 150 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>To</label>
          <input type="date" className="tl-select" value={toDate}
            onChange={e => setToDate(e.target.value)} style={{ minWidth: 150 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Client</label>
          <MultiSelectFilter
            options={clientOptions}
            selected={filterClients}
            onChange={v => { setFilterClients(v); setFilterAgreements([]); }}
            placeholder="All Clients"
            minWidth={180}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Agreement</label>
          <MultiSelectFilter
            options={agreementOptions}
            selected={filterAgreements}
            onChange={setFilterAgreements}
            placeholder="All Agreements"
            minWidth={180}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={labelStyle}>Status</label>
          <select className="tl-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ minWidth: 150 }}>
            <option value="">All Statuses</option>
            {Object.entries(TASK_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        {isMultiUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={labelStyle}>Assignee</label>
            <MultiSelectFilter
              options={memberOptions}
              selected={filterMembers}
              onChange={setFilterMembers}
              placeholder="All Members"
              minWidth={180}
            />
          </div>
        )}
      </div>

      {/* Summary cards */}
      {!loading && agreementGroups.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Agreements',  value: agreementGroups.length },
            { label: 'Tasks',       value: taskCount },
            { label: 'Total Hours', value: grandTotalHours },
            { label: 'Period',      value: periodLabel },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 20px', minWidth: 120 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 3 }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '12px 16px', color: '#ef4444', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : agreementGroups.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)' }}>
          No time logged on agreement tasks for {periodLabel}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-card)', borderBottom: '2px solid var(--border)' }}>
                <Th>S.No</Th>
                <Th>Client Name</Th>
                <Th>Agreement Name</Th>
                <Th>Task ID</Th>
                <Th>Task</Th>
                <Th>Status</Th>
                {isMultiUser && <Th>Assignee</Th>}
                <Th>First Log</Th>
                <Th>Last Log</Th>
                <Th right>Time Taken (hrs)</Th>
              </tr>
            </thead>
            <tbody>
              {agreementGroups.map((ag, ai) => {
                const groupBg = ai % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)';
                return ag.tasks.flatMap((task, ti) => task.rows.map((row, ri) => {
                  const firstOfAg   = ti === 0 && ri === 0;
                  const firstOfTask = ri === 0;
                  const lastOfTask  = ri === task.rows.length - 1;
                  const lastOfAg    = ti === ag.tasks.length - 1 && lastOfTask;
                  return (
                    <tr key={`${ag.agreement_id}-${task.task_id}-${row.logger_user_id}`}
                      style={{ background: groupBg,
                        borderBottom: lastOfAg ? '2px solid var(--border)'
                          : lastOfTask ? '1px solid var(--border)' : '1px solid rgba(255,255,255,0.04)' }}>

                      <td style={{ ...tdStyle, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {firstOfAg ? ai + 1 : ''}
                      </td>

                      <td style={{ ...tdStyle, color: 'var(--text-primary)', maxWidth: 200 }}>
                        {firstOfAg ? ag.client_name : ''}
                      </td>

                      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)', maxWidth: 220 }}>
                        {firstOfAg ? ag.agreement_name : ''}
                        {lastOfAg && ag.tasks.length > 1 && (
                          <div style={{ marginTop: firstOfAg ? 4 : 0, fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                            Agreement total: <strong style={{ color: 'var(--brand)' }}>{formatHoursMinutes(ag.total_minutes)}</strong>
                          </div>
                        )}
                      </td>

                      <td style={{ ...tdStyle, color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {firstOfTask ? (task.task_number || `#${task.task_id}`) : ''}
                      </td>

                      <td style={{ ...tdStyle, color: 'var(--text-primary)', maxWidth: 260 }}>
                        {firstOfTask ? task.title : ''}
                        {lastOfTask && task.rows.length > 1 && (
                          <div style={{ marginTop: firstOfTask ? 4 : 0, fontSize: 11, color: 'var(--text-muted)' }}>
                            Task total: <strong style={{ color: 'var(--brand)' }}>{formatHoursMinutes(task.total_minutes)}</strong>
                          </div>
                        )}
                      </td>

                      <td style={tdStyle}>
                        {firstOfTask && <TaskStatusBadge status={task.status} />}
                      </td>

                      {isMultiUser && (
                        <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {memberName(row.logger_user_id)}
                          {row.logger_user_id === task.assigned_to_user_id && (
                            <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600,
                              color: 'var(--brand)', background: 'rgba(var(--brand-rgb,99,102,241),0.12)',
                              padding: '1px 6px', borderRadius: 10 }}>
                              current
                            </span>
                          )}
                        </td>
                      )}

                      <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {row.first_log_date ? formatDate(row.first_log_date) : '—'}
                      </td>

                      <td style={{ ...tdStyle, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {row.last_log_date ? formatDate(row.last_log_date) : '—'}
                      </td>

                      <td style={{ ...tdStyle, fontWeight: 700, color: 'var(--brand)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatHoursMinutes(row.user_minutes)}
                      </td>
                    </tr>
                  );
                }));
              })}
            </tbody>

            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-card)' }}>
                <td colSpan={isMultiUser ? 9 : 8}
                  style={{ padding: '10px 14px', fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'right' }}>
                  Grand Total
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 800, color: 'var(--brand)', textAlign: 'right', fontSize: 15 }}>
                  {grandTotalHours}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ContractReports() {
  const { appId } = useParams();
  const [activeTab, setActiveTab] = useState('hours');

  return (
    <div className="tasks-page">
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">Agreements Reports</h1>
          <p className="tl-subtitle">Hours logged against agreements, compliance tasks, SLA performance, workload, and revenue by client.</p>
        </div>
      </div>

      <div className="setup-tabs" role="tablist" style={{ marginBottom: 0 }}>
        {[
          { key: 'hours',      label: 'Agreement Hours Report' },
          { key: 'compliance', label: 'Compliance Reports'     },
        ].map(tab => (
          <button
            key={tab.key}
            role="tab"
            className={`setup-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 24 }}>
        {activeTab === 'hours'      && <AgreementHoursTab    appId={appId} />}
        {activeTab === 'compliance' && <ComplianceReportsTab appId={appId} />}
      </div>
    </div>
  );
}
